import { useState } from "react";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  StarOutlined,
  ThunderboltOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { useGetPackagesByStatus } from "../hooks/useGetRecruiterPackage";

interface ChoosePlanPageProps {
  onBack: () => void;
  onSelectPlan: (plan: any, billingCycle: "monthly" | "annual") => void;
  currentPlanName: string;
  isCorporate: boolean;
  currentPlanId: string;
  subscriptionData: any;
}

export const ChoosePlanPage = ({
  onBack,
  onSelectPlan,
  subscriptionData,
}: ChoosePlanPageProps) => {
  const { data: packages, isLoading: loadingPackages } =
    useGetPackagesByStatus();

  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "monthly",
  );

  const mappedPlans =
    packages?.packages?.map((pkg: any) => ({
      id: pkg.package_id,
      name: pkg.package_name,
      description: pkg.package_description,
      monthlyPrice: Number(pkg.monthly_price),
      quarterlyPrice: Number(pkg.quarterly_price),
      biannuallyPrice: Number(pkg.biannually_price),
      annualPrice: Number(pkg.annually_price),
      monthlyPriceFormatted: `₦${Number(pkg.monthly_price).toLocaleString()}`,
      quarterlyPriceFormatted: `₦${Number(pkg.quarterly_price).toLocaleString()}`,
      biannuallyPriceFormatted: `₦${Number(pkg.biannually_price).toLocaleString()}`,
      annualPriceFormatted: `₦${Number(pkg.annually_price).toLocaleString()}`,
      features: pkg.rules?.map((rule: any) => rule.description) || [],
      icon: pkg.package_name.toLowerCase().includes("basic") ? (
        <StarOutlined />
      ) : pkg.package_name.toLowerCase().includes("premium") ? (
        <ThunderboltOutlined />
      ) : pkg.package_name.toLowerCase().includes("enterprise") ? (
        <CrownOutlined />
      ) : (
        <StarOutlined />
      ),
      iconBg: pkg.package_name.toLowerCase().includes("basic")
        ? "bg-[#0B0F5B]"
        : pkg.package_name.toLowerCase().includes("premium")
          ? "bg-purple-600"
          : pkg.package_name.toLowerCase().includes("enterprise")
            ? "bg-amber-600"
            : "bg-pink-600",
      iconColor: "text-white",
    })) || [];

  const sortedPlans = [...mappedPlans].sort(
    (a, b) => a.monthlyPrice - b.monthlyPrice,
  );

  const currentPlanName = subscriptionData?.package_name || "";
  const currentPlanId = subscriptionData?.package_id || "";

  const currentPlanIndex = sortedPlans.findIndex(
    (plan) => plan.id === currentPlanId,
  );

  const getPlanAction = (plan: any, planIndex: number) => {
    if (!subscriptionData) return "Upgrade";

    if (plan.id === currentPlanId) {
      return "Current Plan";
    }

    if (currentPlanIndex === -1) return "Upgrade";

    if (planIndex > currentPlanIndex) return "Upgrade";
    if (planIndex < currentPlanIndex) return "Downgrade";

    return "Upgrade";
  };

  const isButtonDisabled = (plan: any) => {
    return plan.id === currentPlanId;
  };

  if (loadingPackages) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <span
            onClick={onBack}
            className="cursor-pointer flex items-center hover:text-gray-800"
          >
            <ArrowLeftOutlined className="mr-1 text-xs" />
            Pricing
          </span>
          <span className="mx-2">/</span>
          <span className="text-gray-900">Choose Plan</span>
        </div>

        <div className="bg-primary p-8 rounded-lg text-center">
          <h1 className="text-2xl font-bold text-white mb-2">
            Choose Your Plan
          </h1>
          <p className="text-sm text-white/90 max-w-1xl mx-auto">
            Scale your recruitment with flexible pricing. Upgrade or downgrade
            anytime.
          </p>
        </div>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-10">
        <div className="bg-gray-100 p-1 rounded-full flex items-center">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-6 py-2 rounded-full text-sm ${
              billingCycle === "monthly"
                ? "bg-[#0B0F5B] text-white"
                : "text-gray-600"
            }`}
          >
            Monthly
          </button>

          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-6 py-2 rounded-full text-sm flex items-center gap-2 ${
              billingCycle === "annual"
                ? "bg-[#0B0F5B] text-white"
                : "text-gray-600"
            }`}
          >
            Annual
            <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">
              Save 17%
            </span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="space-y-6">
        {sortedPlans.map((plan: any, index: number) => {
          const isCurrentPlan = plan.id === currentPlanId;
          const isPopular =
            plan.name.toLowerCase().includes("premium") ||
            (sortedPlans.length > 2 &&
              index === Math.floor(sortedPlans.length / 2));
          const action = getPlanAction(plan, index);
          const isDisabled = isButtonDisabled(plan);

          return (
            <div
              key={plan.id}
              className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                isCurrentPlan
                  ? "border-green-500 border-2"
                  : "border-gray-200 border-t-4 border-t-primary"
              }`}
            >
              {isCurrentPlan && (
                <div className="bg-green-50 px-6 py-2 text-xs text-green-700 font-medium">
                  ✓ YOUR CURRENT PLAN
                </div>
              )}

              <div className="flex flex-col lg:flex-row">
                {/* LEFT */}
                <div className="w-full lg:w-[30%] p-6 border-b lg:border-b-0 lg:border-r">
                  {isCurrentPlan && !isPopular && (
                    <span className="inline-block mb-3 text-xs bg-green-100 text-green-600 px-2 py-1 rounded-md">
                      ✓ Active Plan
                    </span>
                  )}

                  {isPopular && !isCurrentPlan && (
                    <span className="inline-block mb-3 text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-md">
                      ⚡ Most Popular
                    </span>
                  )}

                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${plan.iconBg}`}
                    >
                      <span className={`text-lg ${plan.iconColor}`}>
                        {plan.icon}
                      </span>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold">{plan.name}</h3>
                      <p className="text-sm text-gray-500">
                        {plan.description}
                      </p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-2xl font-bold">
                      {billingCycle === "monthly"
                        ? plan.monthlyPriceFormatted
                        : plan.annualPriceFormatted}
                    </p>
                    <p className="text-xs text-gray-500">
                      /{billingCycle === "monthly" ? "Month" : "Year"}
                    </p>
                    {billingCycle === "annual" && plan.quarterlyPrice > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Or {plan.quarterlyPriceFormatted}/quarter
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() =>
                      !isDisabled && onSelectPlan(plan, billingCycle)
                    }
                    disabled={isDisabled}
                    className={`w-full py-2 rounded-md transition-colors ${
                      isDisabled
                        ? "bg-[#7C7FA6] text-white cursor-not-allowed"
                        : action === "Upgrade"
                          ? "bg-primary hover:bg-[#0B0F5B]-700 text-white"
                          : action === "Downgrade"
                            ? "bg-primary hover:bg-yellow-700 text-white"
                            : "bg-[#0B0F5B] hover:bg-[#0B0F5B]/90 text-white"
                    }`}
                  >
                    {action}
                  </button>

                  {action === "Downgrade" && (
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Downgrade available at end of billing cycle
                    </p>
                  )}
                </div>

                {/* RIGHT */}
                <div className="flex-1 p-6">
                  <p className="text-xs font-semibold text-gray-500 mb-4">
                    ✓ WHAT'S INCLUDED
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {plan.features.map((feature: string, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-green-50 text-gray-700 text-sm px-3 py-2 rounded-md"
                      >
                        <CheckCircleFilled className="text-green-500 text-xs" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
